import React, { useState, useMemo, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import FullscreenEcharts from "../pl-by-program/FullscreenEcharts";
import Loading from "../../../../common-components/Loading";
import {
  getFilterBusinessUnitsAPI,
  getLaborUtilizationBuUtilizationAPI,
} from "../../../../api/reports-api";
import {
  TABLE_FONT as REPORT_FONT,
  TH_BG_BAND as HEADER_BG,
} from "../../reportTableConfig";

/* ══════════════════════════════════════════════════════════════ */
/*  Fallback styles (used when no classes prop is passed)         */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  tableCard: {
    background: "#FFFFFF",
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,0.08)",
    overflow: "hidden",
    marginBottom: 20,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: REPORT_FONT,
    fontSize: "11px",
    tableLayout: "auto",
  },
  tableWrap: { overflowX: "auto", overflowY: "hidden" },
  buFilterRow: {
    display: "flex",
    alignItems: "center",
    padding: "14px 24px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "#FFFFFF",
    gap: 10,
  },
  buFilterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
  },
  buFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: REPORT_FONT,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  buFilterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 160,
    height: "100%",
    fontFamily: REPORT_FONT,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },
  buChartCard: {
    background: "#fff",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    paddingBottom: "8px",
  },
  buChartTitle: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 40px 10px 14px",
    fontSize: 10,
    fontWeight: 600,
    color: "#FFFFFF",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: REPORT_FONT,
    background: HEADER_BG,
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    flexShrink: 0,
  },
  maximizeBtn: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    border: "none",
    background: "transparent",
    padding: 0,
    lineHeight: 0,
    cursor: "pointer",
    color: "#FFFFFF",
    transition: "color 0.15s",
    "&:hover": { color: "#F59E0B" },
    "&:focus-visible": {
      outline: "2px solid #F59E0B",
      outlineOffset: 2,
      borderRadius: 4,
    },
  },
  buTableCard: {
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,0.06)",
    marginBottom: 10,
  },
  thBu: {
    background: HEADER_BG,
    color: "#fff",
    padding: "7px 12px",
    fontWeight: 700,
    fontSize: "11px",
    textAlign: "left",
    whiteSpace: "nowrap",

  },
  thBuNum: {
    background: HEADER_BG,
    color: "#fff",
    padding: "7px 12px",
    fontWeight: 700,
    fontSize: "11px",
    textAlign: "right",
    whiteSpace: "nowrap",

  },
  tdBuMonth: {
    padding: "5px 12px",
    fontSize: "11px",
    color: "#334155",
    borderBottom: "1px solid #E9EDF3",

    whiteSpace: "nowrap",
    fontWeight: 500,
  },
  tdBuNum: {
    padding: "5px 12px",
    fontSize: "11px",
    color: "#334155",
    textAlign: "right",
    borderBottom: "1px solid #E9EDF3",

    whiteSpace: "nowrap",
  },
  tdBuTotalLabel: {
    padding: "5px 12px",
    fontSize: "11px",
    fontWeight: 700,
    color: "#111111",
    borderTop: "2px solid #C8D0DC",

    background: "#E4EBF3",
  },
  tdBuTotalNum: {
    padding: "5px 12px",
    fontSize: "11px",
    fontWeight: 700,
    color: "#111111",
    textAlign: "right",
    borderTop: "2px solid #C8D0DC",

    background: "#E4EBF3",
  },
  buFootnote: {
    fontSize: "10px",
    color: "#64748B",
    padding: "12px 20px",
    lineHeight: 1.6,
    background: "#F8F9FB",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    fontFamily: REPORT_FONT,
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Helper                                                        */
/* ══════════════════════════════════════════════════════════════ */
function MaximizeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3"  y1="21" x2="10" y2="14" />
    </svg>
  );
}


const NEG_COLOR = "#DC2626";

function fmtN(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${abs})` : abs;
}

function negStyle(v) {
  const n = Number(v);
  return Number.isFinite(n) && n < 0 ? { color: NEG_COLOR } : undefined;
}

/* ══════════════════════════════════════════════════════════════ */
/*  BUUtilizationTab component                                    */
/* ══════════════════════════════════════════════════════════════ */
const MONTH_KEYS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function BUUtilizationTab({ classes, filters, onMeta }) {
  const [buOptions, setBuOptions] = useState([]);
  const [selectedBU, setSelectedBU] = useState("");
  const [loading, setLoading] = useState(true);
  const [apiRows, setApiRows] = useState([]);
  const chartRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getFilterBusinessUnitsAPI()
      .then((res) => {
        if (cancelled) return;
        const seen = new Set();
        const unique = (res?.data || [])
          .map((v) => v.business_unit)
          .filter((b) => b != null && b !== "" && !seen.has(b) && seen.add(b));
        setBuOptions(unique);
        setSelectedBU((prev) => (prev && unique.includes(prev) ? prev : unique[0] || ""));
        if (!unique.length) setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedBU) return;
    let cancelled = false;
    setLoading(true);
    getLaborUtilizationBuUtilizationAPI({ ...filters, bu: [selectedBU] })
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        if (d) {
          setApiRows(d.rows || []);
          onMeta?.({ actualThroughMonth: d.actualThroughMonth, fiscalYear: d.fiscalYear });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [JSON.stringify(filters), selectedBU]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableRows = useMemo(() => {
    const byMonth = new Map(apiRows.map((r) => [r.monthName, r]));
    return MONTH_KEYS.map((month) => {
      const r = byMonth.get(month) || {};
      const rawPct = Number(r.utilizationPct);
      return {
        month,
        hours:       r.hoursActualAndForecast ?? 0,
        employees:   r.noOfEmployees ?? 0,
        stdHours:    r.standardHours ?? 0,
        totalStdHrs: r.totalStandardHours ?? 0,
        utilPct:     Number.isFinite(rawPct) ? rawPct * 100 : 0,
      };
    });
  }, [apiRows]);

  const chartOption = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        formatter: (p) => `${p[0].name}<br/>Util: ${p[0].value.toFixed(1)}%`,
      },
      grid: { top: 20, right: 30, bottom: 50, left: 70 },
      xAxis: {
        type: "category",
        data: tableRows.map((r) => r.month),
        axisLabel: { fontSize: 10, color: "#64748B" },
        axisLine: { lineStyle: { color: "#E0E5EC" } },
        axisTick: { show: false },
        name: "MonthName",
        nameLocation: "center",
        nameGap: 30,
        nameTextStyle: { fontSize: 10, color: "#94A3B8" },
      },
      yAxis: {
        type: "value",
        min: 65,
        max: 100,
        name: "Utilization%",
        nameRotate: 90,
        nameLocation: "center",
        nameGap: 55,
        nameTextStyle: { fontSize: 10, color: "#94A3B8" },
        axisLabel: {
          formatter: (v) => `${v}%`,
          fontSize: 10,
          color: "#64748B",
        },
        splitLine: { lineStyle: { color: "#F0F2F5", type: "dashed" } },
      },
      series: [
        {
          type: "line",
          data: tableRows.map((r) => +r.utilPct.toFixed(2)),
          lineStyle: { color: "#1E88E5", width: 2.5 },
          symbol: "circle",
          symbolSize: 8,
          itemStyle: { color: "#1E88E5" },
          label: {
            show: true,
            formatter: (p) => `${Math.round(p.value)}%`,
            fontSize: 11,
            fontWeight: 600,
            color: "#334155",
            backgroundColor: "rgba(255,255,255,0.88)",
            borderRadius: 3,
            padding: [2, 5],
            borderColor: "#CBD5E1",
            borderWidth: 1,
          },
        },
      ],
    }),
    [tableRows],
  );

  if (loading) return <Loading />;

  return (
    <div>
      {/* ── BU filter ── */}
      <div className={classes.buFilterRow}>
        <div className={classes.buFilterGroup}>
          <span className={classes.buFilterLabel}>BU</span>
          <select
            className={classes.buFilterSelect}
            value={selectedBU}
            onChange={(e) => setSelectedBU(e.target.value)}
          >
            {buOptions.map((bu) => (
              <option key={bu} value={bu}>{bu}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── chart ── */}
      <div className={classes.buChartCard}>
        <div className={classes.buChartTitle}>
          Monthly Utilization Percentages
          <button
            className={classes.maximizeBtn}
            onClick={() => chartRef.current?.toggleFullscreen()}
            aria-label="View Fullscreen"
            title="View Fullscreen"
          >
            <MaximizeIcon />
          </button>
        </div>
        <FullscreenEcharts
          ref={chartRef}
          option={chartOption}
          height={260}
          showButton={false}
        />
      </div>

      {/* ── data table ── */}
      <div className={classes.buTableCard}>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.thBu}>MonthName</th>
                <th className={classes.thBuNum}>Hours Actual &amp; Forecast</th>
                <th className={classes.thBuNum}>No. of employees</th>
                <th className={classes.thBuNum}>Standard Hours</th>
                <th className={classes.thBuNum}>Total Standard Hours</th>
                <th className={classes.thBuNum}>Utilization%</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr
                  key={row.month}
                  style={{ background: i % 2 === 0 ? "#fff" : "#F4F6F9" }}
                >
                  <td
                    className={classes.tdBuMonth}
                    style={{ background: "inherit" }}
                  >
                    {row.month}
                  </td>
                  <td
                    className={classes.tdBuNum}
                    style={{ background: "inherit", ...negStyle(row.hours) }}
                  >
                    {fmtN(row.hours)}
                  </td>
                  <td
                    className={classes.tdBuNum}
                    style={{ background: "inherit" }}
                  >
                    {row.employees}
                  </td>
                  <td
                    className={classes.tdBuNum}
                    style={{ background: "inherit" }}
                  >
                    {row.stdHours}
                  </td>
                  <td
                    className={classes.tdBuNum}
                    style={{ background: "inherit" }}
                  >
                    {row.totalStdHrs.toLocaleString("en-US")}
                  </td>
                  <td
                    className={classes.tdBuNum}
                    style={{ background: "inherit" }}
                  >
                    {row.utilPct.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── footnote ── */}
      <div className={classes.buFootnote}>
        ***1.&nbsp; Utilization % = Billable Hours / Pro-rated Available
        hours.&nbsp;&nbsp; 2.&nbsp; Standard hours for all those rows have been
        excluded where billable hours were zero.&nbsp;&nbsp; 3.&nbsp; Pro-rated
        available hours have been calculated based on Hire Date and Exit Date.
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Default export                                                */
/* ══════════════════════════════════════════════════════════════ */
export default function BUUtilization({ classes: externalClasses, filters, onMeta }) {
  const ownClasses = useStyles();
  return <BUUtilizationTab classes={externalClasses || ownClasses} filters={filters} onMeta={onMeta} />;
}
